import io from 'socket.io-client';
import { AppState, AppStateStatus } from 'react-native';
import { tokenManager } from './token-manager';
import { getCurrentApiConfig } from '../config/api.config';

// Define Socket type
type Socket = ReturnType<typeof io>;

export interface SocketEvent {
  event: string;
  handler: (data: any) => void;
}

export class SocketService {
  private socket: Socket | null = null;
  private baseURL: string;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private pendingListeners: Map<string, Set<(data: any) => void>> = new Map();
  // Keep-alive: mobile OSes drop the socket whenever the app is
  // backgrounded, and a failed first connect (e.g. auth token not hydrated
  // yet on cold start) used to leave the whole session without real-time.
  private keepAliveEnabled = false;
  private keepAliveTimer: ReturnType<typeof setTimeout> | null = null;
  private keepAliveDelay = 2000;
  private appStateSubscription: { remove: () => void } | null = null;

  constructor() {
    this.baseURL = getCurrentApiConfig().baseURL;
  }

  /**
   * Keeps the connection alive for the signed-in session: reconnects when
   * the app returns to the foreground and retries a failed connect with
   * backoff (capped at 30s). Call once after sign-in; stopKeepAlive() on
   * sign-out.
   */
  startKeepAlive(): void {
    if (this.keepAliveEnabled) return;
    this.keepAliveEnabled = true;
    this.appStateSubscription = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active') {
        this.keepAliveDelay = 2000;
        this.ensureConnected();
      }
    });
    this.ensureConnected();
  }

  stopKeepAlive(): void {
    this.keepAliveEnabled = false;
    this.appStateSubscription?.remove();
    this.appStateSubscription = null;
    if (this.keepAliveTimer) clearTimeout(this.keepAliveTimer);
    this.keepAliveTimer = null;
  }

  private ensureConnected(): void {
    if (!this.keepAliveEnabled || this.socket?.connected || this.isConnecting) return;
    if (this.keepAliveTimer) {
      clearTimeout(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
    // An existing socket whose manager gave up / was dropped while
    // backgrounded: refresh its auth and reopen it in place so listeners
    // stay attached; otherwise build a fresh connection.
    if (this.socket && !this.socket.connected) {
      (this.socket as any).auth = { token: tokenManager.getAuthToken() };
      this.socket.connect();
      this.scheduleKeepAliveCheck();
      return;
    }
    this.connect()
      .then(() => {
        this.keepAliveDelay = 2000;
      })
      .catch(() => {
        this.scheduleKeepAliveCheck();
      });
  }

  private scheduleKeepAliveCheck(): void {
    if (!this.keepAliveEnabled || this.keepAliveTimer) return;
    const delay = this.keepAliveDelay;
    this.keepAliveDelay = Math.min(this.keepAliveDelay * 2, 30000);
    this.keepAliveTimer = setTimeout(() => {
      this.keepAliveTimer = null;
      if (!this.socket?.connected) this.ensureConnected();
    }, delay);
  }

  // Connect to Socket.IO server
  async connect(): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.isConnecting) {
      return new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (this.socket?.connected) {
            resolve(this.socket);
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    this.isConnecting = true;

    try {
      const authToken = tokenManager.getAuthToken();
      
      if (!authToken) {
        throw new Error('No authentication token available');
      }

      this.socket = io(this.baseURL, {
        auth: { token: authToken },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        // Never give up while the app is running — a phone switching
        // networks or waking from background needs many attempts.
        reconnectionAttempts: Infinity,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 15000,
      });

      // socket.io-client v4: reconnection events fire on the Manager
      // (socket.io), not the socket. Refresh the auth token before each
      // attempt so a token rotated while disconnected doesn't keep failing.
      this.socket.io.on('reconnect_attempt', (attemptNumber: number) => {
        this.reconnectAttempts = attemptNumber;
        (this.socket as any).auth = { token: tokenManager.getAuthToken() };
      });
      this.socket.io.on('reconnect', () => {
        this.reconnectAttempts = 0;
      });

      this.setupEventHandlers();

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        this.socket!.on('connect', () => {
          clearTimeout(timeout);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          resolve();
        });

        this.socket!.on('connect_error', async (error: any) => {
          clearTimeout(timeout);
          this.isConnecting = false;
          
          // Handle JWT expiration during connection
          if (error.message?.includes('jwt expired') || error.message?.includes('jwt')) {
            try {
              const refreshResult = await tokenManager.refreshTokens();
              if (refreshResult) {
                this.disconnect();
                // Retry connection with new token
                try {
                  await this.connect();
                  resolve();
                  return;
                } catch (retryError) {
                  reject(retryError);
                  return;
                }
              }
            } catch (refreshError) {
              // Token refresh failed
            }
          }
          
          reject(error);
        });
      });

      return this.socket;
    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('auth_error', async (error: any) => {
      if (error.message === 'Token expired' || error.message === 'Invalid token' || 
          error.message?.includes('jwt expired') || error.message?.includes('jwt')) {
        try {
          const refreshResult = await tokenManager.refreshTokens();
          if (refreshResult) {
            this.disconnect();
            await this.connect();
          } else {
            this.disconnect();
          }
        } catch (refreshError) {
          this.disconnect();
        }
      }
    });

    // Listen for centralized notifications from backend
    // Server emits `app-notification` (user-specific room). Keep `notification` for legacy.
    this.socket.on('app-notification', (data: any) => {
      console.log('🔔 [SocketService] App-notification received:', data);
      this.emit('app-notification', data);
    });

    this.socket.on('notification', (data: any) => {
      console.log('🔔 [SocketService] Legacy notification received:', data);
      this.emit('app-notification', data);
    });

    // Attach any pending listeners registered before socket was connected
    this.attachPendingListeners();

    this.socket.on('disconnect', (reason: any) => {
      this.isConnecting = false;
      
      if (reason === 'io server disconnect') {
        this.reconnect();
      }
    });

    // A transport drop while backgrounded is retried by the manager; this
    // just makes sure keep-alive notices if the server closed us outright.
    this.socket.on('disconnect', () => {
      this.scheduleKeepAliveCheck();
    });
  }

  private async reconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    try {
      await this.connect();
    } catch (error) {
      setTimeout(() => this.reconnect(), this.reconnectDelay * Math.pow(2, this.reconnectAttempts));
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  on(event: string, handler: (data: any) => void): void {
    // Always store handler so it can be attached after connect/reconnect
    if (!this.pendingListeners.has(event)) {
      this.pendingListeners.set(event, new Set());
    }
    this.pendingListeners.get(event)!.add(handler);

    if (this.socket) {
      this.socket.on(event, handler);
    }
  }

  off(event: string, handler?: (data: any) => void): void {
    if (handler) {
      this.pendingListeners.get(event)?.delete(handler);
      if (this.pendingListeners.get(event)?.size === 0) {
        this.pendingListeners.delete(event);
      }
    } else {
      this.pendingListeners.delete(event);
    }

    if (this.socket) {
      if (handler) {
        this.socket.off(event, handler);
      } else {
        this.socket.off(event);
      }
    }
  }

  private attachPendingListeners(): void {
    if (!this.socket) return;
    for (const [event, handlers] of this.pendingListeners.entries()) {
      for (const handler of handlers) {
        this.socket.on(event, handler);
      }
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  // Handle JWT expiration errors
  async handleTokenExpiration(): Promise<boolean> {
    try {
      const refreshResult = await tokenManager.refreshTokens();
      if (refreshResult) {
        this.disconnect();
        await this.connect();
        return true;
      } else {
        this.disconnect();
        return false;
      }
    } catch (error) {
      this.disconnect();
      return false;
    }
  }

  /**
   * Listen for new message events
   */
  onNewMessage(handler: (data: any) => void): void {
    this.on('new-message', (data) => {
      handler(data);
    });
  }

  /**
   * Remove new message event listener
   */
  offNewMessage(handler?: (data: any) => void): void {
    this.off('new-message', handler);
  }

  /**
   * Listen for conversation update events
   */
  onConversationUpdate(handler: (data: any) => void): void {
    this.on('conversation-update', handler);
  }

  /**
   * Remove conversation update event listener
   */
  offConversationUpdate(handler?: (data: any) => void): void {
    this.off('conversation-update', handler);
  }

  /**
   * Join a conversation room for real-time updates
   */
  joinConversation(conversationId: string): void {
    this.emit('join-conversation', { conversationId });
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(conversationId: string): void {
    this.emit('leave-conversation', { conversationId });
  }

  /**
   * Join business room for business-wide updates
   */
  joinBusiness(businessId: string): void {
    console.log('🔍 [SocketService] Joining business room:', businessId);
    this.emit('join-business', { businessId });
  }

  /**
   * Leave business room
   */
  leaveBusiness(businessId: string): void {
    console.log('🔍 [SocketService] Leaving business room:', businessId);
    this.emit('leave-business', { businessId });
  }

  /**
   * Join business room and listen for confirmation
   */
  joinBusinessWithConfirmation(businessId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Business join timeout'));
      }, 5000);

      const onJoinedBusiness = (data: any) => {
        if (data.businessId === businessId) {
          clearTimeout(timeout);
          this.socket?.off('joined-business', onJoinedBusiness);
          console.log('✅ [SocketService] Successfully joined business room:', businessId);
          resolve();
        }
      };

      this.socket.on('joined-business', onJoinedBusiness);
      this.emit('join-business', { businessId });
    });
  }

  /**
   * Send typing indicator
   */
  sendTyping(conversationId: string, isTyping: boolean): void {
    this.emit('typing', { conversationId, isTyping });
  }

  /**
   * Listen for new contact events
   */
  onNewContact(handler: (data: any) => void): void {
    this.on('new-contact', (data) => {
      console.log('🔍 [SocketService] New contact received:', data);
      handler(data);
    });
  }

  /**
   * Remove new contact event listener
   */
  offNewContact(handler?: (data: any) => void): void {
    this.off('new-contact', handler);
  }

  /**
   * Listen for contact update events
   */
  onContactUpdate(handler: (data: any) => void): void {
    this.on('contact-update', (data) => {
      console.log('🔍 [SocketService] Contact update received:', data);
      handler(data);
    });
  }

  /**
   * Remove contact update event listener
   */
  offContactUpdate(handler?: (data: any) => void): void {
    this.off('contact-update', handler);
  }
}

export const socketService = new SocketService();
